import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { TLeadTask, TMyTasksFilters, TUpdateTaskPayload } from "@/models/tasks.model";
import { TPaginationMeta } from "@/models/msc.model";
import { getMyTasks, getOwnTasks } from "@/services/tasks.service";
import {
  toggleLeadTaskStatus,
  updateLeadTaskFull,
  removeLeadTask,
} from "@/services/leads.service";
import { getErrorFromBackend } from "@/utils/errors";
import { useTasksRefreshStore } from "@/store/tasks/tasksRefresh.store";

const PAGE_SIZE = 25;

export const useMyTasks = (
  filters: TMyTasksFilters = {},
  // "company" hits GET /task (needs tasks.viewAll); "me" hits GET /task/me,
  // which any user can call for their own tasks.
  // fetchAll: walk every page and merge results — for consumers (e.g. the
  // calendar) that need the complete task set rather than one page at a time.
  options: { scope?: "company" | "me"; fetchAll?: boolean } = {}
) => {
  const scope = options.scope ?? "company";
  const fetchAll = options.fetchAll ?? false;
  const { enqueueSnackbar } = useSnackbar();
  const refreshVersion = useTasksRefreshStore((s) => s.version);
  const bumpRefresh = useTasksRefreshStore((s) => s.bump);
  const [tasks, setTasks] = useState<TLeadTask[]>([]);
  const [meta, setMeta] = useState<TPaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TLeadTask | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Stringify so primitive-equal objects don't recreate the callback
  const filtersKey = JSON.stringify(filters);

  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      const fetch = scope === "me" ? getOwnTasks : getMyTasks;
      const baseFilters = { pageSize: PAGE_SIZE, ...JSON.parse(filtersKey) };

      if (!fetchAll) {
        const response = await fetch(baseFilters);
        setTasks(response.data);
        setMeta(response.meta);
        return;
      }

      let page = 1;
      let allData: TLeadTask[] = [];
      let latestMeta: TPaginationMeta | null = null;
      let hasMore = true;
      do {
        const response = await fetch({ ...baseFilters, page });
        allData = allData.concat(response.data);
        latestMeta = response.meta;
        hasMore = page < response.meta.totalPages;
        page += 1;
      } while (hasMore);
      setTasks(allData);
      setMeta(latestMeta);
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    } finally {
      setIsLoading(false);
    }

  }, [enqueueSnackbar, filtersKey, scope, fetchAll]);

  useEffect(() => {
    fetchTasks();
     
  }, [fetchTasks, refreshVersion]);

  const handleToggleTask = async (taskId: number) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task?.leadId) return;

    const newStatus = !task.isComplete;

    // Optimistic update
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, isComplete: newStatus } : t))
    );

    try {
      await toggleLeadTaskStatus({ leadId: task.leadId, taskId, isComplete: newStatus });
      enqueueSnackbar("Task status updated", { variant: "success" });
      bumpRefresh();
    } catch (error) {
      // Rollback
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, isComplete: !newStatus } : t))
      );
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    }
  };

  const handleUpdateTask = async (taskId: number, data: TUpdateTaskPayload): Promise<void> => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task?.leadId) return;

    // Backend rejects null values — strip keys whose value is null or undefined
    const payload = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined && v !== null)
    ) as TUpdateTaskPayload;

    try {
      await updateLeadTaskFull({ leadId: task.leadId, taskId, data: payload });
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, ...data } : t))
      );
      enqueueSnackbar("Task updated successfully", { variant: "success" });
      bumpRefresh();
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    }
  };

  const handleDeleteTask = async (taskId: number): Promise<void> => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task?.leadId) return;

    try {
      setIsDeleting(true);
      await removeLeadTask({ leadId: task.leadId, taskId });
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      enqueueSnackbar("Task deleted successfully", { variant: "success" });
      bumpRefresh();
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleComplete = async (taskId: number): Promise<void> => {
    await handleToggleTask(taskId);
  };

  return {
    tasks,
    meta,
    isLoading,
    isDeleting,
    selectedTask,
    setSelectedTask,
    showCreateModal,
    setShowCreateModal,
    handleToggleTask,
    handleUpdateTask,
    handleDeleteTask,
    handleToggleComplete,
    fetchTasks,
    bumpTasksRefresh: bumpRefresh,
  };
};
